import React, {useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,} from '@mui/material';
import {useFormik} from 'formik';
import * as yup from 'yup';
import {toast} from 'react-toastify';
import {AxiosError} from 'axios';
import {useApiService} from '../../../../../context/ApiServiceContext';
import CustomInput from '../../../../shared/form_controls/CustomInput';
import {StringKeys} from '../../../../../utils/types';

interface GenericEditModalProps<T> {
    entity: T;
    open: boolean;
    onClose: () => void;
    onEntityEdited: (entity: T) => void;
    apiEndpoint: string;
    dialogTitle: string;
    successMessage: string;
    errorMessage: string;
    placeholder: string;
    inputError: string;
}

const GenericEditModal = <T extends { id: string; name: string }>({
                                                          entity,
                                                          open,
                                                          onClose,
                                                          onEntityEdited,
                                                          apiEndpoint,
                                                          dialogTitle,
                                                          successMessage,
                                                          errorMessage,
                                                          placeholder,
                                                          inputError
                                                      }: GenericEditModalProps<T>) => {
    const apiService = useApiService();
    const [isLoading, setIsLoading] = useState(false);

    const folderSchema = yup.object().shape({
        name: yup.string().required(inputError),
    });

    interface FormValues {
        name: string;
    }

    const formFields = [
        {
            id: 'name',
            name: 'name',
            label: placeholder,
            type: 'text',
            errorExpression: (formik: any) => formik.touched.name && !!formik.errors.name,
            errorText: (formik: any) => formik.errors.name,
            sx: { mb: 2 },
            disabled: false,
        },
    ];

    const formik = useFormik<FormValues>({
        initialValues: { name: entity.name },
        validationSchema: folderSchema,
        onSubmit: (values) => {
            setIsLoading(true);
            apiService.api
                .put<T>(`${apiEndpoint}/${entity.id}`, values)
                .then((response) => {
                    onEntityEdited(response.data);
                    toast.success(successMessage);
                    onClose();
                    setIsLoading(false);
                })
                .catch((e: AxiosError<{ code: string }>) => {
                    console.error(e);
                    toast.error(errorMessage);
                    setIsLoading(false);
                });
        },
        validateOnBlur: true,
        validateOnMount: true,
        enableReinitialize: true,
    });

    return (
        <Dialog
            open={open}
            onClose={() => {
                onClose();
                formik.resetForm();
            }}
        >
            <DialogTitle>{dialogTitle}</DialogTitle>
            <form noValidate onSubmit={formik.handleSubmit}>
                <DialogContent>
                    {formFields.map((field, index) => (
                        <FormControl key={index} fullWidth sx={field.sx}>
                            <CustomInput
                                id={field.id}
                                name={field.name as string}
                                label={field.label}
                                type={field.type}
                                value={formik.values[field.name as StringKeys<FormValues>]}
                                disabled={field.disabled || false}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={field.errorExpression(formik)}
                                errorText={field.errorText(formik)}
                            />
                        </FormControl>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isLoading}>
                        סגור
                    </Button>
                    <Button type="submit" color="primary" disabled={isLoading}>
                        שמור
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default GenericEditModal;
